import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardBody,
  VStack,
  HStack,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  useColorModeValue
} from '@chakra-ui/react';
import {
  FiEye,
  FiUserPlus,
  FiThumbsUp,
  FiShare2,
  FiDollarSign
} from 'react-icons/fi';
// DashboardMetrics interface is defined locally below
import IconWrapper from './IconWrapper';

interface Metrics {
  totalViews: number;
  totalSubscribers: number;
  totalLikes: number;
  totalShares: number;
  totalDonations: number;
  totalRevenue: number;
  viewsChange: number;
  subscribersChange: number;
  likesChange: number;
  revenueChange: number;
}

interface DashboardMetricsProps {
  metrics: Metrics;
  isLoading?: boolean;
}

const DashboardMetrics: React.FC<DashboardMetricsProps> = ({ metrics, isLoading = false }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  const metricsData = [
    {
      label: 'Views',
      value: metrics.totalViews.toLocaleString(),
      icon: FiEye,
      color: 'blue.500',
      helpText: 'Total video views'
    },
    {
      label: 'Subscribers',
      value: `+${metrics.totalSubscribers.toLocaleString()}`,
      icon: FiUserPlus,
      color: 'green.500',
      helpText: 'New subscribers',
      trend: metrics.totalSubscribers > 0 ? 'increase' : 'neutral'
    },
    {
      label: 'Likes',
      value: metrics.totalLikes.toLocaleString(),
      icon: FiThumbsUp,
      color: 'red.500',
      helpText: 'Total likes received'
    },
    {
      label: 'Shares',
      value: metrics.totalShares.toLocaleString(),
      icon: FiShare2,
      color: 'purple.500',
      helpText: 'Content shares'
    },
    {
      label: 'Support Received',
      value: `$${metrics.totalDonations.toLocaleString()}`,
      icon: FiDollarSign,
      color: 'yellow.500',
      helpText: 'Donations received'
    }
  ];

  if (isLoading) {
    return (
      <Grid templateColumns={{ base: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }} gap={{ base: 3, md: 4 }}>
        {[...Array(5)].map((_, index) => (
          <Card key={index} bg={cardBg} shadow="sm">
            <CardBody p={{ base: 3, md: 4 }}>
              <VStack align="stretch" gap={2}>
                <HStack justify="space-between">
                  <Box w={{ base: 6, md: 8 }} h={{ base: 6, md: 8 }} bg="gray.200" borderRadius="md" />
                  <Text fontSize={{ base: 'xs', md: 'sm' }} color={textColor}>Loading...</Text>
                </HStack>
                <Box h={{ base: 6, md: 8 }} bg="gray.200" borderRadius="md" />
              </VStack>
            </CardBody>
          </Card>
        ))}
      </Grid>
    );
  }

  return (
    <Grid templateColumns={{ base: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }} gap={{ base: 3, md: 4 }}>
      {metricsData.map((metric, index) => (
        <Card key={index} bg={cardBg} shadow="sm" _hover={{ shadow: 'md' }} transition="all 0.2s">
          <CardBody p={{ base: 3, md: 4 }}>
            <VStack align="stretch" gap={2}>
              <HStack justify="space-between" align="center">
                <IconWrapper icon={metric.icon} size={24} color={metric.color} />
                <Text fontSize={{ base: 'xs', md: 'sm' }} color={textColor} fontWeight="medium" textAlign="right">
                  {metric.label}
                </Text>
              </HStack>
              
              <Stat>
                <StatNumber fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold" color={metric.color}>
                  {metric.value}
                </StatNumber>
                <StatHelpText fontSize={{ base: '2xs', md: 'xs' }} color={textColor}>
                  {metric.helpText}
                </StatHelpText>
              </Stat>
            </VStack>
          </CardBody>
        </Card>
      ))}
    </Grid>
  );
};

export default DashboardMetrics;
